import React, { useEffect, useState } from 'react';
import { Button, Spinner, Toast, Select } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  GetCrewListBrief,
  GetCrewListJobs,
  CrewListBriefResponse,
  CrewListDay,
  CrewListCallTime,
  job,
} from '../../services/dtos';

const CrewListBriefPage: React.FC = () => {
  const client = apiServices.client;

  const [jobs, setJobs] = useState<job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [brief, setBrief] = useState<CrewListBriefResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setJobsLoading(true);
    client
      .get(new GetCrewListJobs())
      .then((data: job[]) => {
        setJobs(data);
        setJobsLoading(false);
      })
      .catch(() => {
        setError('Failed to load jobs');
        setJobsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedJobId) {
      setBrief(null);
      return;
    }
    setLoading(true);
    setError(null);
    setBrief(null);
    const req = new GetCrewListBrief();
    req.jobId = selectedJobId;
    client
      .get(req)
      .then((data: CrewListBriefResponse) => {
        setBrief(data);
        setLoading(false);
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to load brief');
        setLoading(false);
      });
  }, [selectedJobId]);

  const downloadExcel = async () => {
    if (!selectedJobId) return;
    setDownloading(true);
    setError(null);
    try {
      const url = `${apiServices.apiUrl()}/job/${selectedJobId}/crewlist/export`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const show = brief?.header?.show?.trim().replace(/[^\w\-]/g, '_') || '';
      a.download = `${selectedJobId}${show ? '_' + show : ''}_CrewList.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      setError('Excel download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto w-full flex flex-col gap-6 dark:text-white">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Crew List Brief</h2>
        {brief && (
          <Button color="success" size="sm" onClick={downloadExcel} disabled={downloading}>
            {downloading ? <Spinner size="sm" /> : 'Download Excel'}
          </Button>
        )}
      </div>

      {error && <Toast color="failure">{error}</Toast>}

      <div>
        <label className="block font-semibold mb-2">Select Job:</label>
        {jobsLoading ? (
          <Spinner color="info" />
        ) : (
          <Select
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
          >
            <option value="">Choose a job</option>
            {jobs.map((j) => (
              <option key={j.job_ID} value={j.job_ID}>
                {j.show} ({j.start_Date ? j.start_Date.slice(0, 10) : j.job_ID})
              </option>
            ))}
          </Select>
        )}
      </div>

      {loading && <Spinner color="info" />}

      {brief && <BriefView brief={brief} />}
    </div>
  );
};

// ── Brief view ────────────────────────────────────────────────────────────

function BriefView({ brief }: { brief: CrewListBriefResponse }) {
  const h = brief.header;
  return (
    <div className="flex flex-col gap-6">
      {/* Header card */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <div className="col-span-2 text-lg font-bold text-gray-900 dark:text-white mb-1">{h.show}</div>
        <HeaderField label="Employer" value={h.employer} />
        <HeaderField label="Site" value={h.site} />
        <HeaderField label="Location" value={h.location} />
        <HeaderField label="Payroll" value={[h.payrollCompany, h.payrollPhone].filter(Boolean).join(' · ')} />
        <HeaderField label="Call By" value={h.callBy} />
        <HeaderField label="Steward" value={[h.stewardName, h.stewardPhone].filter(Boolean).join(' · ')} />
      </div>

      {/* Days */}
      {brief.days.map((day) => (
        <DaySection key={day.dayNumber} day={day} />
      ))}

      {/* Totals */}
      <div className="text-xs text-gray-500 dark:text-gray-400 border-t pt-3 flex gap-6">
        <span><strong>{brief.totals.totalDispatches}</strong> dispatches</span>
        <span><strong>{brief.totals.uniqueMembers}</strong> unique people</span>
        <span><strong>{brief.totals.totalDays}</strong> days</span>
        <span><strong>{brief.totals.totalCallTimes}</strong> call times</span>
      </div>
    </div>
  );
}

function HeaderField({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label} </span>
      <span className="text-gray-900 dark:text-white">{value || '—'}</span>
    </div>
  );
}

function DaySection({ day }: { day: CrewListDay }) {
  const dateLabel = day.date
    ? new Date(day.date.replace(' ', 'T')).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        timeZone: 'UTC',
      })
    : '';

  return (
    <div className="flex flex-col gap-0 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* Day header */}
      <div className="bg-gray-900 dark:bg-gray-800 text-white px-4 py-2 flex items-center justify-between">
        <span className="font-bold text-sm">DAY {day.dayNumber} — {dateLabel}</span>
        <span className="text-xs text-gray-300">{day.totalCrew} total</span>
      </div>

      {/* Call time sections */}
      {day.callTimes.map((ct, i) => (
        <CallTimeSection key={i} ct={ct} />
      ))}
    </div>
  );
}

function CallTimeSection({ ct }: { ct: CrewListCallTime }) {
  const timeLabel = ct.startTime ? formatTime(ct.startTime) : 'TBD';
  return (
    <div>
      {/* Section header */}
      <div className="bg-gray-100 dark:bg-gray-800 px-4 py-1.5 flex items-center gap-4 border-t border-gray-200 dark:border-gray-700">
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
          Start: {timeLabel}
        </span>
        <span className="text-sm font-semibold text-red-600 dark:text-red-400">{ct.crewCount} crew</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{ct.skillSummary}</span>
      </div>

      {/* Roster table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-3 py-1.5 text-right w-10">Pos</th>
              <th className="px-3 py-1.5 w-28">Skill</th>
              <th className="px-3 py-1.5 w-40">Name</th>
              <th className="px-3 py-1.5 w-32">Phone</th>
              <th className="px-3 py-1.5">Email</th>
              <th className="px-3 py-1.5">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {ct.roster.map((r, i) => (
              <tr key={i} className={`even:bg-gray-50 dark:even:bg-gray-900/40 ${r.isPlaceholder ? 'opacity-60' : ''}`}>
                <td className="px-3 py-1.5 text-right text-gray-400 text-xs">{r.position}</td>
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 text-xs">{r.skill}</td>
                <td className={`px-3 py-1.5 font-medium ${r.isPlaceholder ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                  {r.isPlaceholder ? '— TBD —' : (r.name || '—')}
                </td>
                <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">
                  {r.isPlaceholder ? '—' : (r.phone || '—')}
                </td>
                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 text-xs">
                  {r.isPlaceholder ? '' : (r.email || '—')}
                </td>
                <td className="px-3 py-1.5 text-gray-400 dark:text-gray-500 text-xs italic">{r.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatTime(dbTime: string): string {
  const d = new Date(`1970-01-01T${dbTime}`);
  if (isNaN(d.getTime())) {
    // Try parsing as full datetime string
    const d2 = new Date(dbTime);
    if (isNaN(d2.getTime())) return dbTime;
    return d2.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default CrewListBriefPage;
